import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react'
import ScrollHint from 'scroll-hint'

const DRAG_THRESHOLD = 5
export const TABLE_LEFT_ALIGN_MAX_WIDTH = 720

export const getScheduleConfirmDefaultTableAreaWidth = (viewportWidth: number): number => {
    if (viewportWidth > 800) {
        return viewportWidth - ((viewportWidth - 720) / 2 + 20)
    }
    if (viewportWidth > 599) {
        return viewportWidth - 60
    }
    return viewportWidth - 20
}

export const getScheduleConfirmExpandedTableAreaWidth = (viewportWidth: number): number => {
    return viewportWidth - 16
}

export const getScheduleConfirmTableGridMargins = (viewportWidth: number) => ({
    marginLeft: {
        xs: '0px',
        sm: viewportWidth <= 800 ? '-40px' : 'calc(0px - ((100vw - 720px) / 2))',
    },
    marginRight: {
        xs: '0px',
        sm: viewportWidth <= 800 ? '-40px' : 'calc(0px - ((100vw - 720px) / 2))',
    },
})

export const getScheduleConfirmTableContainerPadding = (
    viewportWidth: number,
    isWideTable: boolean,
    needsExpansion: boolean,
) => {
    if (needsExpansion || isWideTable) {
        return {
            paddingLeft: { xs: '20px', sm: '20px' },
            paddingRight: { xs: '20px', sm: '20px' },
        }
    }

    return {
        paddingLeft: {
            xs: '20px',
            sm: viewportWidth <= 800 ? '60px' : 'calc(((100vw - 720px) / 2) + 20px)',
        },
        paddingRight: {
            xs: '0px',
            sm: viewportWidth <= 800 ? '60px' : 'calc(((100vw - 720px) / 2) + 20px)',
        },
    }
}

type UseHorizontalTableScrollOptions = {
    viewportWidth: number
    getDefaultAreaWidth?: (viewportWidth: number) => number
    getExpandedAreaWidth?: (viewportWidth: number) => number
    deps?: unknown[]
}

export const horizontalTableScrollableSx = {
    width: '100%',
    maxWidth: '100%',
    overflowX: 'scroll',
    overflowY: 'hidden',
    justifyContent: 'flex-start',
    scrollbarGutter: 'stable',
    WebkitOverflowScrolling: 'touch',
    '::-webkit-scrollbar': {
        display: 'block',
        height: 10,
    },
    '::-webkit-scrollbar-thumb': {
        backgroundColor: 'rgba(0, 0, 0, 0.35)',
        borderRadius: 5,
    },
    '::-webkit-scrollbar-track': {
        backgroundColor: 'rgba(0, 0, 0, 0.08)',
    },
    scrollbarWidth: 'thin',
    scrollbarColor: 'rgba(0, 0, 0, 0.35) rgba(0, 0, 0, 0.08)',
    MsOverflowStyle: 'auto',
} as const

export function useHorizontalTableScroll({
    viewportWidth,
    getDefaultAreaWidth = getScheduleConfirmDefaultTableAreaWidth,
    getExpandedAreaWidth = getScheduleConfirmExpandedTableAreaWidth,
    deps = [],
}: UseHorizontalTableScrollOptions) {
    const containerRef = useRef<HTMLDivElement>(null)
    const tableRef = useRef<HTMLTableElement>(null)
    const [isWideTable, setIsWideTable] = useState(false)
    const [needsExpansion, setNeedsExpansion] = useState(false)
    const [needsScroll, setNeedsScroll] = useState(false)
    const [isDragging, setIsDragging] = useState(false)
    const dragState = useRef({
        startX: 0,
        scrollLeft: 0,
        moved: false,
        pointerId: -1,
        active: false,
    })
    const justDraggedRef = useRef(false)

    const measureOverflow = useCallback(() => {
        const table = tableRef.current
        if (!table) {
            setIsWideTable(false)
            setNeedsExpansion(false)
            setNeedsScroll(false)
            return
        }

        const tableWidth = table.scrollWidth
        const defaultAreaWidth = getDefaultAreaWidth(viewportWidth)
        const expandedAreaWidth = getExpandedAreaWidth(viewportWidth)

        setIsWideTable(tableWidth > TABLE_LEFT_ALIGN_MAX_WIDTH)
        setNeedsExpansion(tableWidth > defaultAreaWidth + 1)
        setNeedsScroll(tableWidth > expandedAreaWidth + 1)
    }, [viewportWidth, getDefaultAreaWidth, getExpandedAreaWidth])

    useLayoutEffect(() => {
        measureOverflow()

        const resizeObserver = new ResizeObserver(measureOverflow)
        if (tableRef.current) {
            resizeObserver.observe(tableRef.current)
        }

        window.addEventListener('resize', measureOverflow)

        return () => {
            resizeObserver.disconnect()
            window.removeEventListener('resize', measureOverflow)
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [measureOverflow, viewportWidth, ...deps])

    useLayoutEffect(() => {
        const container = containerRef.current
        if (!container) {
            return
        }

        if (!needsScroll) {
            container.style.overflowX = ''
            return
        }

        if (!document.querySelector('.js-scrollable [data-target="scrollable-icon"]')) {
            new ScrollHint('.js-scrollable', {
                i18n: {
                    scrollable: 'スクロールできます',
                },
            })
        }

        container.style.overflowX = 'scroll'
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [needsScroll, viewportWidth, ...deps])

    const handlePointerDown = useCallback(
        (event: React.PointerEvent<HTMLDivElement>) => {
            if (!needsScroll || !containerRef.current) {
                return
            }

            const target = event.target as HTMLElement
            if (target.closest('input, textarea, button, a, select, [contenteditable="true"]')) {
                return
            }

            dragState.current = {
                startX: event.clientX,
                scrollLeft: containerRef.current.scrollLeft,
                moved: false,
                pointerId: event.pointerId,
                active: true,
            }
        },
        [needsScroll],
    )

    useEffect(() => {
        if (!needsScroll) {
            return
        }

        const handlePointerMove = (event: PointerEvent) => {
            const container = containerRef.current
            if (!container || !dragState.current.active || dragState.current.pointerId !== event.pointerId) {
                return
            }

            const deltaX = event.clientX - dragState.current.startX
            if (!dragState.current.moved && Math.abs(deltaX) > DRAG_THRESHOLD) {
                dragState.current.moved = true
                setIsDragging(true)
                container.setPointerCapture(event.pointerId)
            }

            if (dragState.current.moved) {
                container.scrollLeft = dragState.current.scrollLeft - deltaX
            }
        }

        const endDrag = (event: PointerEvent) => {
            if (!dragState.current.active || dragState.current.pointerId !== event.pointerId) {
                return
            }

            if (dragState.current.moved) {
                justDraggedRef.current = true
            }

            const container = containerRef.current
            if (container && dragState.current.moved) {
                try {
                    container.releasePointerCapture(event.pointerId)
                } catch {
                    // Pointer may already be released.
                }
            }

            dragState.current = {
                startX: 0,
                scrollLeft: 0,
                moved: false,
                pointerId: -1,
                active: false,
            }
            setIsDragging(false)
        }

        window.addEventListener('pointermove', handlePointerMove)
        window.addEventListener('pointerup', endDrag)
        window.addEventListener('pointercancel', endDrag)

        return () => {
            window.removeEventListener('pointermove', handlePointerMove)
            window.removeEventListener('pointerup', endDrag)
            window.removeEventListener('pointercancel', endDrag)
        }
    }, [needsScroll])

    const guardClick = useCallback((callback: () => void) => {
        if (justDraggedRef.current) {
            justDraggedRef.current = false
            return
        }
        callback()
    }, [])

    return {
        containerRef,
        tableRef,
        isWideTable,
        needsExpansion,
        needsScroll,
        isDragging,
        handlePointerDown,
        guardClick,
    }
}
